import { useState, useEffect, useCallback, useMemo } from "react";

import { AuthEvents } from "src/platform/constants";
import { FeatureFlagNames } from "src/platform/constants/FeatureFlags";
import { eventBus } from "src/platform/events/EventBus";
import { featureFlags } from "src/platform/feature-flags/FeatureFlags";
import debugLogger from "src/platform/utils/debugLogger";

import { AuthApi } from "../api/authApi";
import { AuthDataContract } from "../contracts/authDataContract";
import { AuthState, LoginCredentials, User } from "../types/auth.types";

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: false,
    error: null,
    isAuthenticated: false,
  });

  const api = useMemo(() => new AuthApi(eventBus), []);
  const contract = useMemo(() => new AuthDataContract(), []);

  useEffect(() => {
    if (!featureFlags.isEnabled(FeatureFlagNames.NEW_AUTH_MODULE)) {
      return;
    }

    // Note: GlobalEventBus has no unsubscribeGlobal — subscriptions persist for
    // the lifetime of the singleton. This matches the pattern in useOrganization.
    // The session JWT lives in an httpOnly cookie; we never store it in JS.
    eventBus.subscribeGlobal(
      AuthEvents.LOGIN_SUCCESS,
      (payload: { user: User }) => {
        setState({
          user: payload.user,
          loading: false,
          error: null,
          isAuthenticated: true,
        });
        debugLogger.info("use-auth", "Login success", payload.user?.email);
      },
    );

    eventBus.subscribeGlobal(AuthEvents.LOGOUT_SUCCESS, () => {
      setState({
        user: null,
        loading: false,
        error: null,
        isAuthenticated: false,
      });
      debugLogger.info("use-auth", "Logout success");
    });

    eventBus.subscribeGlobal(
      AuthEvents.LOGIN_FAILED,
      (payload: { error: string }) => {
        setState((prev) => ({ ...prev, loading: false, error: payload.error }));
        debugLogger.error("use-auth", "Login failed", payload.error);
      },
    );

    // Validate the httpOnly cookie session via /auth/me.
    // The token is in the cookie, not accessible to JS.
    setState((prev) => ({ ...prev, loading: true }));
    void api.fetchCurrentUser().catch(() => {
      setState((prev) => ({
        ...prev,
        loading: false,
        isAuthenticated: false,
      }));
    });
  }, [api]);

  const login = useCallback(
    (credentials: LoginCredentials) => {
      if (!featureFlags.isEnabled(FeatureFlagNames.NEW_AUTH_MODULE)) return;
      if (!contract.validateInput(credentials)) {
        setState((prev) => ({
          ...prev,
          error: contract.getErrorMessages().join(", "),
        }));
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null }));
      void api
        .login(contract.transformInput(credentials))
        .catch((err: Error) => {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err.message ?? "Login failed",
          }));
        });
    },
    [api, contract],
  );

  const logout = useCallback(() => {
    if (!featureFlags.isEnabled(FeatureFlagNames.NEW_AUTH_MODULE)) return;
    void api.logout();
  }, [api]);

  return { ...state, login, logout };
};
